import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  fetchServicePlan,
  fetchContactInfo,
  selectServicePlan,
  selectContactInfo,
  selectBillingLoading,
  selectContactInfoLoading,
} from '../../redux/slices/billingSlice/billingSlice';

import {
  selectServicePlanWorkflowState,
  selectCurrentWorkflowStepIndex,
  selectSelectedServicePlan,
  selectCurrentWorkflowDefinition,
  selectIsServicePlanWorkflowSubmitting,
  updateSingleField,
  nextStep,
  previousStep,
  setStepIndex,
  clearServicePlanWorkflowSlice,
  setCurrentWorkflow,
  submitServicePlanWorkflow,
} from '../../redux/slices/servicePlanWorkflowSlice/servicePlanWorkflowSlice';

import SERVICE_PLAN_WORKFLOW_TYPE, {
  SERVIVE_PLAN_WORKFLOW_COMPONENT,
  SERVICE_PLAN_PAYMENT_METHOD,
  SERVICE_PLAN_PAYMENT_PERIOD,
} from '../../utility/types/ServicePlanWorkflowTypes';

import PlanDetailsTableConnected from './ChangePlanPage/PlanDetailsTableConnected';
import SelectedPlanInfoCard from './BillingPage/SelectedPlanInfoCard';
import ServicePlanSelectionForm from './BillingPage/forms/ServicePlanSelectionForm';
import ServicePlanSummaryForm from './BillingPage/forms/ServicePlanSummaryForm';

const STEP_TITLES = {
  [SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICESELECTION]: 'Choose Your Plan',
  [SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICEPLANOPTIONS]: 'Plan Options',
  [SERVIVE_PLAN_WORKFLOW_COMPONENT.SUMMARYANDPAYMENT]: 'Summary & Payment',
};

function ServicePlanWorkflowManager({ selectedWorkflow, countryList }) {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();

  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [isTaxLoading, setIsTaxLoading] = useState(false);
  const paymentMethodDefaulted = useRef(false);

  const activeServicePlan = useSelector(selectServicePlan);
  const contactInfo = useSelector(selectContactInfo);
  const billingLoading = useSelector(selectBillingLoading);
  const contactInfoLoading = useSelector(selectContactInfoLoading);

  // Targeted selectors — manager only re-renders on navigation events, not on field changes
  const currentWorkflowStepIndex = useSelector(selectCurrentWorkflowStepIndex);
  const selectedServicePlan = useSelector(selectSelectedServicePlan);
  const workflowDef = useSelector(selectCurrentWorkflowDefinition);
  const isSubmitting = useSelector(selectIsServicePlanWorkflowSubmitting);

  const steps = workflowDef.steps || [];
  const currentStep = steps[currentWorkflowStepIndex];
  const isLastStep = currentWorkflowStepIndex >= steps.length - 1;
  const includesPlanSelection = steps.includes(SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICESELECTION);

  const activePlanTier = activeServicePlan ? activeServicePlan.subscriptionTier : null;
  const activePlanTierId = activePlanTier ? activePlanTier.id : null;

  const preselectActiveTier = Boolean(
    history.location.state && history.location.state.preselectActiveTier,
  );
  const preselectAppliedRef = useRef(false);

  useEffect(() => {
    dispatch(setCurrentWorkflow(selectedWorkflow));
    return () => dispatch(clearServicePlanWorkflowSlice());
  }, [dispatch, selectedWorkflow]);

  useEffect(() => {
    if (!activeServicePlan) dispatch(fetchServicePlan());
    if (!contactInfo) dispatch(fetchContactInfo());
  }, [dispatch]);

  useEffect(() => {
    if (!activeServicePlan) return;

    const wf = selectServicePlanWorkflowState(store.getState());
    if (!wf.currentServicePlan) {
      dispatch(updateSingleField({ field: 'currentServicePlan', value: activeServicePlan }));

      if (activeServicePlan.recurringInterval) {
        const isYearly = activeServicePlan.recurringInterval.toLowerCase().includes('year');
        const isChangePaymentPeriod = selectedWorkflow
          === SERVICE_PLAN_WORKFLOW_TYPE.CHANGEPAYMENTPERIOD;
        const billingCycle = (isYearly !== isChangePaymentPeriod)
          ? SERVICE_PLAN_PAYMENT_PERIOD.YEARLY
          : SERVICE_PLAN_PAYMENT_PERIOD.MONTHLY;
        dispatch(updateSingleField({ field: 'serviceSelection.selectedBillingCycle', value: billingCycle }));
      }

      if (selectedWorkflow.id === SERVICE_PLAN_WORKFLOW_TYPE.PAYINADVANCE.id) {
        dispatch(updateSingleField({ field: 'serviceSelection.additionalYears', value: 1 }));
      }
    }
  }, [activeServicePlan]);

  // For workflows that skip plan selection, seed selectedServicePlan from the active plan's tier
  useEffect(() => {
    const wf = selectServicePlanWorkflowState(store.getState());
    if (includesPlanSelection || wf.selectedServicePlan) return;
    if (!activePlanTier) return;
    dispatch(updateSingleField({ field: 'selectedServicePlan', value: activePlanTier }));
  }, [activePlanTier, includesPlanSelection, dispatch, store]);

  useEffect(() => {
    if (!preselectActiveTier || preselectAppliedRef.current) return;
    if (!includesPlanSelection || !activePlanTier) return;

    const selectionStepIndex = steps
      .indexOf(SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICESELECTION);
    const nextStepIndex = selectionStepIndex >= 0 ? selectionStepIndex + 1 : 1;

    dispatch(updateSingleField({ field: 'selectedServicePlan', value: activePlanTier }));
    dispatch(setStepIndex(nextStepIndex));
    preselectAppliedRef.current = true;
  }, [preselectActiveTier, includesPlanSelection, activePlanTier, steps, dispatch]);

  useEffect(() => {
    if (paymentMethodDefaulted.current) return;
    const hasCardOnFile = contactInfo && contactInfo.cardOnFile
      && contactInfo.cardOnFile.cardNumber;
    if (hasCardOnFile) {
      dispatch(updateSingleField({
        field: 'paymentMethod.method',
        value: SERVICE_PLAN_PAYMENT_METHOD.CREDIT_CARD_ON_FILE,
      }));
      paymentMethodDefaulted.current = true;
    }
  }, [contactInfo]);

  const goToPlanSelection = () => {
    if (includesPlanSelection) {
      const selectionStepIndex = steps
        .indexOf(SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICESELECTION);
      dispatch(updateSingleField({ field: 'selectedServicePlan', value: null }));
      dispatch(selectionStepIndex >= 0 ? setStepIndex(selectionStepIndex) : previousStep());
      return;
    }

    const { pathname } = history.location;
    const trimmed = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const basePath = trimmed.substring(0, trimmed.lastIndexOf('/'));
    history.push(`${basePath}/change-plan`);
  };

  const handleNext = async () => {
    setHasSubmitError(false);

    if (isLastStep) {
      const result = await dispatch(submitServicePlanWorkflow());

      if (submitServicePlanWorkflow.fulfilled.match(result)) {
        dispatch(clearServicePlanWorkflowSlice());
        history.push('/s/subscription/billing');
        return;
      }

      setHasSubmitError(true);
      return;
    }

    dispatch(nextStep());
  };

  const handlePlanSelect = (plan) => {
    setHasSubmitError(false);
    dispatch(updateSingleField({ field: 'selectedServicePlan', value: plan }));
    dispatch(nextStep());
  };

  const renderStep = () => {
    switch (currentStep) {
      case SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICESELECTION:
        return (
          <PlanDetailsTableConnected
            selectable
            currentPlanId={activePlanTierId}
            selectedPlanId={selectedServicePlan ? selectedServicePlan.id : null}
            onSelectPlan={handlePlanSelect}
          />
        );
      case SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICEPLANOPTIONS:
        return (
          <ServicePlanSelectionForm
            onChangePlanClick={goToPlanSelection}
            countryList={countryList}
          />
        );
      case SERVIVE_PLAN_WORKFLOW_COMPONENT.SUMMARYANDPAYMENT:
        return (
          <ServicePlanSummaryForm
            onChangePlan={goToPlanSelection}
            countryList={countryList}
            onTaxLoadingChange={setIsTaxLoading}
          />
        );
      default: return null;
    }
  };

  if (billingLoading || contactInfoLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  const onPlanSelectionStep = currentStep === SERVIVE_PLAN_WORKFLOW_COMPONENT.SERVICESELECTION;

  const isNextDisabled = isSubmitting
    || isTaxLoading
    || (!selectedServicePlan && !onPlanSelectionStep);

  return (
    <div className="row gx-4 align-items-start">
      <div className={!onPlanSelectionStep && selectedServicePlan ? 'col-xl-9 col-md-8' : 'col'}>
        {hasSubmitError && (
          <div className="alert alert-danger" role="alert">
            An unexpected error has occurred.
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>{STEP_TITLES[currentStep] || 'Service Plan'}</strong>
            <small className="text-muted">
              Step
              {' '}
              {currentWorkflowStepIndex + 1}
              {' '}
              of
              {' '}
              {steps.length}
            </small>
          </div>

          <div className="card-body">
            {renderStep()}
          </div>

          {!onPlanSelectionStep && (
            <div className="card-footer d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={isNextDisabled}
              >
                {isSubmitting && isLastStep && (
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {isLastStep ? 'Submit' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>

      {!onPlanSelectionStep && selectedServicePlan && (
        <div className="col-xl-3 col-md-4 ml-auto">
          <SelectedPlanInfoCard
            planName={selectedServicePlan.name}
            devices={selectedServicePlan.deviceLimit}
            users={selectedServicePlan.userLimit}
            alerts={selectedServicePlan.alertLimit}
            reports={selectedServicePlan.reportLimit}
            dataHistoryValue={selectedServicePlan.dataAgeLimitValue}
            dataHistoryUnit={selectedServicePlan.dataAgeLimitUnit}
            userRoles={selectedServicePlan.supportsRoles || false}
            alertingExtras={selectedServicePlan.hasAdvancedAlerting || false}
            samlLogin={selectedServicePlan.level > 4}
            onComparePlans={() => window.open('/pricing', '_blank')}
          />
        </div>
      )}
    </div>
  );
}

ServicePlanWorkflowManager.propTypes = {
  selectedWorkflow: PropTypes.oneOf([
    SERVICE_PLAN_WORKFLOW_TYPE.NEWSUBSCRIPTION,
    SERVICE_PLAN_WORKFLOW_TYPE.CHANGEPLAN,
    SERVICE_PLAN_WORKFLOW_TYPE.CHANGEPAYMENTPERIOD,
    SERVICE_PLAN_WORKFLOW_TYPE.PAYBALANCE,
    SERVICE_PLAN_WORKFLOW_TYPE.PAYINADVANCE,
  ]).isRequired,
  countryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    crmId: PropTypes.number,
    code: PropTypes.number,
    name: PropTypes.string,
    abbreviation: PropTypes.string,
  })),
};

ServicePlanWorkflowManager.defaultProps = {
  countryList: [],
};

export default ServicePlanWorkflowManager;
